import { useEditorState } from "@/store";

import { getRidersSessionIdFromCookie, isLocal, SessionSocketError } from "@/utils";
import { Logger } from "@/utils";
import { sendPendingAchieves, handleWsAchieveResponse } from "@/utils";
import { isSessionActiveCheck } from "@/utils";
import { isPublicProject } from "@/utils";

import * as Sentry from "@sentry/react";


class SessionSocket {
	public ws: WebSocket | null = null;
	public editorState: typeof useEditorState;

	public retryCount = 0;
	public readonly MAX_RETRY_COUNT = 8;
	public reconnectTimer: NodeJS.Timeout | null = null;

	public closeEventCode = 0;

	private eventListeners: {
		open: ((event: Event) => void)[];
		close: ((event: CloseEvent) => void)[];
		error: ((event: Event) => void)[];
		message: ((event: MessageEvent) => void)[];
	} = {
			open: [],
			close: [],
			error: [],
			message: [],
		};



	constructor(state: typeof useEditorState) {
		this.editorState = state;
		window.addEventListener("beforeunload", () => { this.disconnect(); });
	}
	public connect() {
		try {
			if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
				return;
			}

			const editorSessionId = this.editorState?.getState().metadata?.session_id;
			const ridersSessionId = getRidersSessionIdFromCookie();

			if (!editorSessionId) {
				throw new Error("SessionSocket.editorSessionIdMissing");
			}
			if (!ridersSessionId) {
				throw new Error("SessionSocket.ridersSessionIdMissing");
			}

			const url = this.getWsUrl(ridersSessionId, editorSessionId);
			this.ws = new WebSocket(url);

			this.ws.onopen = (event: Event) => {
				this.eventListeners.open.forEach((fnc) => fnc(event));
			};
			this.ws.onclose = (event: CloseEvent) => {
				this.eventListeners.close.forEach((fnc) => fnc(event));
			};
			this.ws.onerror = (event: Event) => {
				this.eventListeners.error.forEach((fnc) => fnc(event));
			};
			this.ws.onmessage = (event: MessageEvent) => {
				this.eventListeners.message.forEach((fnc) => fnc(event));
			};


		} catch (e) {
			this.disconnect();
			this.reconnect();
			Sentry.captureException(new SessionSocketError("Failed to connect to session socket", e as Error));
		}
	}

	public async isReconnectValid() {
		try {

			if (this.retryCount > 0) {
				// Retry process already started.
				return false;
			} else if (isLocal) {
				return false;
			} else if (isPublicProject()) {
				return false;
			} else if (useEditorState.getState().platform === "lti") {
				return false;
			} else if (
				this.closeEventCode === 4001			//Authentication failed. Your session may have expired. Please refresh the page
				|| this.closeEventCode === 4002		//Missing editor_session_id parameter
				|| this.closeEventCode === 4003		//Editor session not found.
				|| this.closeEventCode === 4004		//Editor session is not active. It may have been stopped.
				|| this.closeEventCode === 4005		//Editor session does not belong to your account.
				|| this.closeEventCode === 4006		//WebSocket connection closed by server.", # disconnect timeout
				|| this.closeEventCode === 4007		//Session stopped due to inactivity.
				|| this.closeEventCode === 4008		//Session stopped by administrator.
				|| this.closeEventCode === 4009		//Session stopped by API action.
			) {
				return false;

			} else if (useEditorState.getState().isBackButtonPressed) {
				return false;

			}
			else if (!(await isSessionActiveCheck(this.editorState.getState().metadata?.session_id))) {
				return false;

			}

			return true;

		} catch (e) {
			Sentry.captureException(new SessionSocketError("Failed to check if reconnect is valid", e as Error));
			return true;
		}
	}

	public isShutdown() {
		if (isLocal) {
			return false;
		} else if (this.retryCount > 0) {
			return false;
		} else if (this.closeEventCode === 4009) {
			return false;
		} else if (useEditorState.getState().isBackButtonPressed) {
			return false;
		} else if (this.closeEventCode === 1000) {
			return false;
		} else if (isPublicProject()) {
			return false;
		} else if (useEditorState.getState().platform === "lti") {
			return false;
		}

		return true
	}

	public async reconnect() {
		const isReconnectValid = await this.isReconnectValid();
		if (!isReconnectValid) {
			if (this.isShutdown()) {
				useEditorState.setState({ shutDown: true });

				Sentry.addBreadcrumb({
					category: "session",
					message: "Initiating automatic session shutdown.",
					level: "info",
					data: {
						retryCount: this.retryCount,
						closeEventCode: this.closeEventCode,
						isBackButtonPressed: useEditorState.getState().isBackButtonPressed,
					}
				});
				Sentry.captureException(new SessionSocketError("Session shutdown"), { level: "info" });
			}
			return;
		}

		this.retryCount++;


		const retry = () => {
			if (this.retryCount >= this.MAX_RETRY_COUNT) {
				useEditorState.setState({ shutDown: true });

				Sentry.addBreadcrumb({
					category: "session",
					message: "Initiating automatic session shutdown.",
					level: "info",
					data: {
						retryCount: this.retryCount,
						closeEventCode: this.closeEventCode
					}
				});
				Sentry.captureException(new SessionSocketError("Session shutdown"), { level: "info" });
				return
			}

			const delay = Math.min(30_000, 1000 * Math.pow(2, this.retryCount));
			this.connect();
			this.reconnectTimer = setTimeout(retry, delay);
			this.retryCount++;
		}

		this.reconnectTimer = setTimeout(retry, 1000);

	}

	public disconnect() {
		if (this.ws) {
			this.ws.close();
			this.ws = null;
		}
	}
	public sendHeartbeat = () => {
		if (useEditorState.getState().environment === "local") {
			return;
		}

		const ws = this.ws;

		if (!ws || ws.readyState !== WebSocket.OPEN) {
			this.reconnect();
			return;
		}

		const payload = {
			type: "heartbeat",
			timestamp: new Date().toISOString(),
		};

		try {
			ws.send(JSON.stringify(payload));
			Logger.log("[EditorSessionWS] heartbeat sent", payload);
		} catch (e) {
			Logger.log("[EditorSessionWS] heartbeat send failed");
			Sentry.captureException(new SessionSocketError("Failed to send heartbeat", e as Error));
		}
	};

	public registerEventListeners(type: "open", fnc: (event: Event) => void): void;
	public registerEventListeners(type: "close", fnc: (event: CloseEvent) => void): void;
	public registerEventListeners(type: "error", fnc: (event: Event) => void): void;
	public registerEventListeners(type: "message", fnc: (event: MessageEvent) => void): void;
	public registerEventListeners(type: "open" | "close" | "error" | "message", fnc: (event: never) => void) {
		switch (type) {
			case "open":
				this.eventListeners.open.push(fnc as (event: Event) => void);
				break;
			case "close":
				this.eventListeners.close.push(fnc as (event: CloseEvent) => void);
				break;
			case "error":
				this.eventListeners.error.push(fnc as (event: Event) => void);
				break;
			case "message":
				this.eventListeners.message.push(fnc as (event: MessageEvent) => void);
				break;
		}
	}

	private getWsUrl(ridersSessionId: string, editorSessionId: string) {
		if (import.meta.env.MODE === "stembox") {
			// Localhost is not secure, so we use ws instead of wss.
			return `ws://${import.meta.env.VITE_COLABSERVER_URL}/ws/editor-session/?sessionid=${encodeURIComponent(
				ridersSessionId,
			)}&editor_session_id=${encodeURIComponent(editorSessionId)}`;
		}

		// Production and Staging use wss.
		return `wss://${import.meta.env.VITE_COLABSERVER_URL}/ws/editor-session/?sessionid=${encodeURIComponent(
			ridersSessionId,
		)}&editor_session_id=${encodeURIComponent(editorSessionId)}`;
	}

}

const sessionSocket = new SessionSocket(useEditorState);
sessionSocket.registerEventListeners("open", () => {
	Logger.log("SessionSocket.open");

	sessionSocket.retryCount = 0;
	if (sessionSocket.reconnectTimer) {
		clearTimeout(sessionSocket.reconnectTimer);
		sessionSocket.reconnectTimer = null;
	}

	sendPendingAchieves(sessionSocket.ws);
});
sessionSocket.registerEventListeners("close", (event: CloseEvent) => {
	Logger.log("SessionSocket.close");
	sessionSocket.closeEventCode = event.code;
	sessionSocket.disconnect();
	sessionSocket.reconnect();
});
sessionSocket.registerEventListeners("error", () => {
	Logger.log("SessionSocket.error");
	sessionSocket.disconnect();
	sessionSocket.reconnect();
});
sessionSocket.registerEventListeners("message", (event: MessageEvent) => {
	const data = JSON.parse(event.data);
	if (data.type === "achieve_result") {
		const achieveCb = sessionSocket.editorState.getState().achieve;
		handleWsAchieveResponse(data, achieveCb);
	}
});

export default sessionSocket;